import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'class-names';
import { connect } from 'react-redux';
import { formatMoney } from '../../functions';
import {
  closeCart,
  openCart,
  populateItems,
  updateItem,
  addItem,
  setGiftWrap,
  unsetGiftWrap,
  updateCart,
} from '../../redux/cart/actions';

import CartRhsItem from './CartRhsItem';
import IconChevronRight from '../icons/IconChevronRight';
import IconClose from '../icons/IconClose';

class CartRhsContent extends Component {
  constructor(props) {
    super(props);

    const {
      populateItems,
      pageLoadItems,
    } = props;

    populateItems(pageLoadItems);
    this.updatePackagingProductsInCart();
  }

  // Hooks ------------
  componentDidMount() {
    this.updateActiveState();

    // Check if we should reopen the cart after a redirect
    const shouldReopenCart = sessionStorage.getItem('reopenCart');
    if (shouldReopenCart === 'true') {
      sessionStorage.removeItem('reopenCart');
      const { openCart } = this.props;
      openCart();
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen, items } = this.props;
    if (!prevProps.isOpen && isOpen) {
      document.body.style.overflow = 'hidden';
    } else if (prevProps.isOpen && !isOpen) {
      document.body.style.overflow = '';
    }
    this.updateActiveState();
    if (items != prevProps.items) {
      this.updateGiftNoteAttr();
    }
  }

  // Getters / computed ------------
  isGiftWrapEnabled() {
    const {
      giftWrapEnabled,
      packagingProduct1,
      packagingProduct2,
    } = this.props;

    return giftWrapEnabled && (packagingProduct1 || packagingProduct2);
  }

  isGwpEnabled() {
    const { gwpEnabled, gwpThreshold } = this.props;
    return gwpEnabled && gwpThreshold;
  }

  gwpProducts() {
    const {
      gwpProduct1,
      gwpProduct2,
      gwpProduct3,
      gwpProduct4,
      gwpDiscountCode1,
      gwpDiscountCode2,
      gwpDiscountCode3,
      gwpDiscountCode4,
    } = this.props;

    const products = [];
    gwpProduct1 && products.push({ ...gwpProduct1, discountCode: gwpDiscountCode1 });
    gwpProduct2 && products.push({ ...gwpProduct2, discountCode: gwpDiscountCode2 });
    gwpProduct3 && products.push({ ...gwpProduct3, discountCode: gwpDiscountCode3 });
    gwpProduct4 && products.push({ ...gwpProduct4, discountCode: gwpDiscountCode4 });
    return products;
  }

  meetsGwpThreshold() {
    const { items, gwpThreshold } = this.props;
    let subtotal = 0;
    items.forEach((item) => {
      subtotal += (item.quantity * item.itemPriceCents);
    });
    // Convert threshold to cents (multiply by 100)
    return subtotal >= (gwpThreshold * 100);
  }

  packagingProducts() {
    const {
      packagingProduct1,
      packagingProduct2,
    } = this.props;

    const value = [];
    packagingProduct1 && value.push(packagingProduct1);
    packagingProduct2 && value.push(packagingProduct2);
    return value;
  }

  packagingProductIds() {
    return this.packagingProducts().map((item) => String(item.id));
  }

  packagingProductsInCart() {
    const { items: cartItems } = this.props;
    const packagingProductIds = this.packagingProductIds();
    const value = cartItems.filter(
      (cartItem) => packagingProductIds.includes(String(cartItem.productId)),
    );

    return value;
  }

  giftNoteItem() {
    const cartItems = (this && this.props) ? this.props.items : [];
    return cartItems.find(({ type }) => type === 'giftnote_giftcard');
  }

  isGiftInCart() {
    return this.isGiftWrapEnabled()
      ? this.packagingProductsInCart().length > 0
      : false;
  }

  computed() {
    return {
      isGiftWrapEnabled: this.isGiftWrapEnabled(),
      packagingProducts: this.packagingProducts(),
      packagingProductIds: this.packagingProductIds(),
      packagingProductsInCart: this.packagingProductsInCart(),
      isGiftInCart: this.isGiftInCart(),
    };
  }

  // Methods ------------
  updateGiftNoteAttr() {
    const giftNoteItem = this.giftNoteItem();
    const { updateCart } = this.props;
    let giftnoteProps = {};

    if (giftNoteItem) {
      // add giftnote cart attributes
      giftnoteProps = giftNoteItem.properties.reduce(
        (res, prop) => {
          if (prop[0] && prop[0].charAt(0) != '_') return res;
          if (prop[0] && (
            prop[0].includes('__giftnote') || prop[0].includes('__giftcard')
          )) {
            const newKey = prop[0]
              .replace('__giftcard', '__giftnote')
              .replace('__giftnote', 'giftnote');
            res[newKey] = prop[1];
          }
          return res;
        }, {},
      );
      giftnoteProps.giftnote_method = 'instant';
    } else {
      // remove giftnote cart attributes
      giftnoteProps = {
        giftnote_medium: '',
        giftnote_method: '',
        giftnote_email: '',
        giftnote_to: '',
        giftnote_message: '',
        giftnote_from: '',
      };
    }

    updateCart({ attributes: giftnoteProps });
  }

  updateActiveState() {
    const { isGiftInCart } = this.computed();
    const { setGiftWrap, unsetGiftWrap } = this.props;
    if (isGiftInCart) {
      setGiftWrap();
    } else {
      unsetGiftWrap();
    }
  }

  updatePackagingProductsInCart() {
    const {
      items: cartItems,
      updateItem,
    } = this.props;
    const {
      isGiftInCart,
      packagingProductsInCart,
    } = this.computed();

    if (isGiftInCart) {
      if (cartItems.length === 1) {
        // Clear cart if only packaging product exists
        packagingProductsInCart.forEach((cartItem, index) => {
          updateItem(cartItem.key, 0);
        });
      } else if (cartItems.length > 1) {
        // Make sure only 1 packaging product is in cart
        packagingProductsInCart.forEach((cartItem, index) => {
          if (index === 0 && cartItem.quantity > 1) {
            updateItem(cartItem.key, 1);
          } else if (index > 0) {
            updateItem(cartItem.key, 0);
          }
        });
      }
    }
  }

  // Render ------------
  renderGwpCallout() {
    const { gwpTitle, gwpDescription, gwpMobileHorizontalScroll } = this.props;
    const gwpProducts = this.gwpProducts();

    if (!this.isGwpEnabled() || !this.meetsGwpThreshold() || gwpProducts.length === 0) {
      return null;
    }

    const productsClassName = classNames('cart-rhs__gwp-products', {
      'cart-rhs__gwp-products--horizontal-scroll': gwpMobileHorizontalScroll === true,
    });

    return (
      <div className="cart-rhs__gwp-callout mb-3">
        <div
          className="cart-rhs__gwp-title"
          dangerouslySetInnerHTML={{ __html: gwpTitle }}
        />
        <div
          className="cart-rhs__gwp-description"
          dangerouslySetInnerHTML={{ __html: gwpDescription }}
        />
        <div className={productsClassName}>
          {gwpProducts.map((product) => (
            <div key={product.id} className="cart-rhs__gwp-product-item">
              <div className="gwp-product-card">
                <a
                  href={`/products/${product.handle}`}
                >
                  <div className="gwp-product-card__image">
                    <img
                      src={product.featured_image}
                      alt={product.title}
                      className="gwp-product-card__img"
                    />
                  </div>
                </a>
                <div className="gwp-product-card__content">
                  <h4 className="gwp-product-card__title">{product.title}</h4>
                  <p className="gwp-product-card__price">
                    <span className="gwp-product-card__free-label">COMPLIMENTARY</span>
                  </p>
                  <button
                    type="button"
                    className="button button--small gwp-product-card__select-btn"
                    onClick={() => this.handleGwpSelect(product)}
                  >
                    Select Gift
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  handleGwpSelect(product) {
    const { addItem } = this.props;

    // Get the first available variant
    const firstVariant = product.variants && product.variants.length > 0
      ? product.variants[0]
      : null;

    if (firstVariant) {
      // Transform options array to match expected structure
      // Expected format: [{ name: "Type", value: "Bath Mat" }, ...]
      const optionsWithValues = [];
      if (product.options && firstVariant.options) {
        product.options.forEach((optionName, index) => {
          if (firstVariant.options[index]) {
            optionsWithValues.push({
              name: optionName,
              value: firstVariant.options[index],
            });
          }
        });
      }

      // Create variant object matching the complete structure from json-cart.liquid
      const variant = {
        variantId: String(firstVariant.id),
        title: product.title,
        info: firstVariant.title,
        itemPriceCents: String(firstVariant.price),
        comparePrice: String(firstVariant.compare_at_price || 0),
        optionsWithValues,
        tags: product.tags || [],
        type: product.type,
        image: product.featured_image,
        imageSrcset: product.featured_image,
        available: firstVariant.available,
        productId: String(product.id),
      };

      // Use Redux addItem action to add to cart
      addItem(firstVariant.id, 1, variant, null, null)
        .then(() => {
          // If there's a discount code, redirect to apply it and come back
          if (product.discountCode) {
            // Set a flag in sessionStorage to reopen cart after redirect
            sessionStorage.setItem('reopenCart', 'true');

            // Build redirect URL to current page
            const currentUrl = window.location.pathname + window.location.search;
            const redirectUrl = `/discount/${encodeURIComponent(product.discountCode)}?redirect=${encodeURIComponent(currentUrl)}`;

            // Redirect to apply discount and return to current page
            window.location.href = redirectUrl;
          }
          // If no discount code, cart drawer stays open automatically
        })
        .catch((error) => {
          console.error('Error adding GWP to cart:', error);
        });
    }
  }

  render() {
    let subtotal = 0;
    const {
      items, isOpen, closeCart, cartLink,
    } = this.props;
    items.forEach((item) => {
      subtotal += (item.quantity * item.itemPriceCents);
    });

    this.updatePackagingProductsInCart();

    return (
      <div role="button" className={classNames('cart-rhs', { 'cart-rhs--open': isOpen })}>
        {/* eslint-disable-next-line max-len */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div aria-label="Close" className="cart-rhs__bg" onClick={closeCart} />
        <div className="cart-rhs__drawer">
          <button type="button" className="cart-rhs__close-button button-text-only" onClick={closeCart}>
            <IconClose />
            <span className="sr-only">Close</span>
          </button>
          <h2>My Order</h2>
          <ul className="cart-rhs__list mb-3">
            {items && items.map((item) => (
              <CartRhsItem
                item={item}
                key={item.key}
                promoBadgeBgColor={this.props.cartPromoBadgeBgColor}
                promoBadgeTextColor={this.props.cartPromoBadgeTextColor}
              />
            ))}
          </ul>
          {this.renderGwpCallout()}
          <p className="text-right mb-3">
            Subtotal:&nbsp;
            <span>{Shopify.currency.active + " " + formatMoney(subtotal)}</span>
          </p>
          <a href={cartLink} className="button">
            Go to Cart
          </a>
          <button className="button cart-rhs__continue-shopping" onClick={closeCart}>
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }
}

CartRhsContent.propTypes = {
  // text: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  isLoading: state.cart.isLoading,
  isOpen: state.cart.isOpen,
  items: state.cart.items,
});

const mapDispatchToProps = {
  closeCart,
  openCart,
  populateItems,
  updateItem,
  addItem,
  setGiftWrap,
  unsetGiftWrap,
  updateCart,
};

CartRhsContent.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  isOpen: PropTypes.bool,
  populateItems: PropTypes.func.isRequired,
  updateItem: PropTypes.func.isRequired,
  addItem: PropTypes.func.isRequired,
  setGiftWrap: PropTypes.func.isRequired,
  unsetGiftWrap: PropTypes.func.isRequired,
  updateCart: PropTypes.func.isRequired,

  pageLoadItems: PropTypes.arrayOf(PropTypes.object),
  attributes: PropTypes.objectOf(PropTypes.any),
  closeCart: PropTypes.func.isRequired,
  openCart: PropTypes.func.isRequired,
  cartLink: PropTypes.string.isRequired,

  giftWrapEnabled: PropTypes.bool,
  packagingProduct1: PropTypes.objectOf(PropTypes.any),
  packagingProduct2: PropTypes.objectOf(PropTypes.any),
  // packagingVariants1: PropTypes.arrayOf(PropTypes.any),
  // packagingVariants2: PropTypes.arrayOf(PropTypes.any),

  gwpEnabled: PropTypes.bool,
  gwpThreshold: PropTypes.number,
  gwpMobileHorizontalScroll: PropTypes.bool,
  gwpTitle: PropTypes.string,
  gwpDescription: PropTypes.string,
  gwpProduct1: PropTypes.objectOf(PropTypes.any),
  gwpProduct2: PropTypes.objectOf(PropTypes.any),
  gwpProduct3: PropTypes.objectOf(PropTypes.any),
  gwpProduct4: PropTypes.objectOf(PropTypes.any),
  gwpDiscountCode1: PropTypes.string,
  gwpDiscountCode2: PropTypes.string,
  gwpDiscountCode3: PropTypes.string,
  gwpDiscountCode4: PropTypes.string,
  cartPromoBadgeBgColor: PropTypes.string,
  cartPromoBadgeTextColor: PropTypes.string,
};

CartRhsContent.defaultProps = {
  items: [],
  pageLoadItems: [],
  isOpen: false,
  attributes: null,

  giftWrapEnabled: false,
  packagingProduct1: null,
  packagingProduct2: null,
  // packagingVariants1: [],
  // packagingVariants2: [],

  gwpEnabled: false,
  gwpThreshold: 200,
  gwpMobileHorizontalScroll: true,
  gwpTitle: '',
  gwpDescription: '',
  gwpProduct1: null,
  gwpProduct2: null,
  gwpProduct3: null,
  gwpProduct4: null,
  gwpDiscountCode1: '',
  gwpDiscountCode2: '',
  gwpDiscountCode3: '',
  gwpDiscountCode4: '',
  cartPromoBadgeBgColor: '#D5BB8C',
  cartPromoBadgeTextColor: '#FFFFFF',
};

export default connect(mapStateToProps, mapDispatchToProps)(CartRhsContent);
